import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { OrganizationInfo } from "../../lib/portal-loaders";

/**
 * Where to go after signing in, when it's a page of this organization's portal - a ?redirect= that points anywhere
 * else (another site, another organization) is ignored, so a crafted sign-in link can't send someone off-site.
 */
export function safePortalRedirect(redirect: string | null | undefined, orgSlug: string): string | null {
  if (!redirect) return null;
  // A path, not a URL: no scheme, no protocol-relative "//host", no backslash tricks
  if (!redirect.startsWith("/") || redirect.startsWith("//") || redirect.includes("\\")) return null;
  const portalRoot = `/org/${orgSlug}/portal/`;
  return redirect.startsWith(portalRoot) ? redirect : null;
}

/**
 * A private knowledge base sends a visitor who isn't signed in to sign in - and back to the page they asked for
 * afterwards, so a shared link to an article still lands on it. Returns true while redirecting.
 */
export function usePrivateKnowledgeBaseRedirect(
  organizationInfo: OrganizationInfo | undefined,
  isKbPublic: boolean,
  isAuthenticated: boolean
): boolean {
  const navigate = useNavigate();
  const mustSignIn = !isKbPublic && !isAuthenticated;
  const orgSlug = organizationInfo?.subdomain;

  useEffect(() => {
    if (mustSignIn && orgSlug) {
      navigate({
        to: "/org/$orgSlug/portal/login",
        params: { orgSlug },
        search: { redirect: window.location.pathname + window.location.search },
      });
    }
  }, [mustSignIn, orgSlug, navigate]);

  return mustSignIn;
}
